/**
 * <s-back-to-top> — floating "back to top" button.
 *
 * Shows its button once the visitor has scrolled past `data-offset` pixels
 * (default 600) and scrolls the page back to the top on click. Smooth scrolling
 * is skipped when the visitor prefers reduced motion.
 *
 * Required markup:
 *   button[data-back-to-top] — the button; starts hidden via the `hidden` attribute.
 */
class SBackToTop extends HTMLElement {
  #ac;
  #button;
  #frame = 0;

  connectedCallback() {
    this.#button = this.querySelector('[data-back-to-top]');
    if (!this.#button) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.#button.addEventListener('click', this.#onClick, { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  get #offset() {
    const value = Number.parseInt(this.dataset.offset ?? '', 10);
    return Number.isFinite(value) ? value : 600;
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    this.#button.hidden = window.scrollY < this.#offset;
  }

  #onClick = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    // Keep keyboard and screen reader users at the top of the page too.
    const target = document.querySelector('main') ?? document.body;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };
}

if (!customElements.get('s-back-to-top')) {
  customElements.define('s-back-to-top', SBackToTop);
}
